// H-5 to H-9: building the round.
//
// The assessment, the onboarding fields and the welcome message, then Open.
// Two things here are not ordinary form state:
//
//   The NOTES under the welcome message are generated, not written. The owner
//   sees them while they edit but cannot change them, and they update live as
//   the settings change: switch the input mode and the voice line switches with
//   it (H-8). They carry the mandatory AI and privacy notices, which is why
//   they are not the owner's to edit.
//
//   Opening the round LOCKS the assessment and the fields, and re-snapshots the
//   role's levels (H-9, H-5). After that an edit to the role does not change
//   how candidates were graded, which is the whole reason the snapshot exists.

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Dialog, Typography } from '@mui/material';
import { hiringApi, hiringMessage, type HiringRoleView, type HiringRoundView } from '../../../../api/hiring';
import { useHiringOrg, useRefreshHiring } from '../../../../queries/useHiring';
import {
  ActionButton,
  BODY,
  BottomBar,
  BRANDY,
  BRANDY_FILL,
  BRANDY_FRAME,
  Chip,
  DANGER,
  FONT,
  INPUT_SX,
  LABEL,
  LINE,
  LINE_2,
  Note,
  Segmented,
  SURFACE,
  TEXT,
  TEXT_2,
  TEXT_3,
  TEXTAREA_SX,
  WELL
} from '../capability-setup/designSystem';
import QuizCard from '../home/elliott-lesson/components/QuizCard';
import { exportAssessmentCsv } from './hiringExport';
import { LEVEL_COLOR, LEVEL_NAME } from '../Program/components/learning-lifecycle/CapabilityGapsList';
import { LEVEL_ORDER } from '../Program/components/learning-lifecycle/teamCapability';
import { CopyLines, GRID_LINE, RowButton } from './hiringParts';
import { MOBILE_SX } from '../../../../mobile/mobileApp';
import RoleplayBrief from '../../../hiring/RoleplayBrief';

/** One part of a capability's assessment: a heading, with everything under it held by a rule. */
const ItemPart: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
    <Typography sx={{ ...LABEL, color: BRANDY }}>{title}</Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', pl: '12px', borderLeft: `1px solid ${BRANDY}` }}>
      {children}
    </Box>
  </Box>
);

/**
 * The four levels an answer to this roleplay is graded on, below the words being
 * edited, so the owner can see what any change still has to allow for. One level
 * to a row, as the descriptions can run long.
 */
const LevelsGuide: React.FC<{ levels: Record<string, string> }> = ({ levels }) => (
  <Box sx={{ border: GRID_LINE, borderRadius: '4px', '& > * + *': { borderTop: GRID_LINE } }}>
    {LEVEL_ORDER.map((level) => (
      <Box
        key={level}
        sx={{
          display: 'grid',
          gridTemplateColumns: '90px minmax(0, 1fr)',
          gap: '10px',
          p: '8px 10px',
          [MOBILE_SX]: { gridTemplateColumns: 'minmax(0, 1fr)', gap: '4px' }
        }}
      >
        <Typography sx={{ ...LABEL, color: LEVEL_COLOR[level] }}>{LEVEL_NAME[level]}</Typography>
        <Typography sx={{ ...BODY, fontSize: 12, color: TEXT_2 }}>{levels[level] || '-'}</Typography>
      </Box>
    ))}
  </Box>
);

/** What Elliott will do with an edit or made of one, and the way back to what it drafted. */
const RoleplayCheckLine: React.FC<{
  item: HiringRoundView['items'][number];
  disabled: boolean;
  onRestore: () => void;
}> = ({ item, disabled, onRestore }) => {
  const edited = item.roleplay !== item.draftRoleplay;
  // An answer only covers the words it was given: edited since, it says nothing.
  const check = edited && item.roleplayCheck?.text === item.roleplay ? item.roleplayCheck : null;
  const message =
    check?.status === 'flagged'
      ? check.reason
      : check?.status === 'unchecked'
      ? "Elliott couldn't check this just now. It will try again when you open the round."
      : check?.status === 'passed'
      ? 'Elliott checked this edit: it can still be graded on all four levels.'
      : "If you make edits, Elliott will review them when you save to ensure the roleplay can still grade against the capability's levels below.";
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '6px', flexWrap: 'wrap' }}>
      <Typography
        role={check?.status === 'flagged' ? 'alert' : undefined}
        sx={{ ...BODY, fontSize: 12, flex: 1, minWidth: 200, color: check?.status === 'flagged' ? DANGER : TEXT_3 }}
      >
        {message}
      </Typography>
      {edited && (
        <Box
          component="button"
          type="button"
          disabled={disabled}
          onClick={onRestore}
          sx={{
            background: 'none',
            border: 0,
            p: 0,
            flexShrink: 0,
            cursor: 'pointer',
            fontFamily: FONT,
            fontSize: 12,
            color: BRANDY,
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
            '&:disabled': { opacity: 0.5, cursor: 'default' }
          }}
        >
          Restore Elliott&apos;s Version
        </Box>
      )}
    </Box>
  );
};

/** Drafting again throws away everything on the screen, so it asks first. */
const DraftAgainDialog: React.FC<{ open: boolean; onConfirm: () => void; onCancel: () => void }> = ({
  open,
  onConfirm,
  onCancel
}) => (
  <Dialog
    open={open}
    onClose={onCancel}
    maxWidth="sm"
    fullWidth
    PaperProps={{ sx: { bgcolor: SURFACE, border: `1px solid ${LINE}`, borderRadius: '8px' } }}
  >
    <Box sx={{ p: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, color: TEXT }}>Draft Again</Typography>
      <Typography sx={{ ...BODY, color: TEXT_2 }}>
        Elliott will regenerate all capabilities again and this version will be replaced.
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
        <ActionButton onClick={onCancel}>Cancel</ActionButton>
        <ActionButton primary onClick={onConfirm}>
          Draft Again
        </ActionButton>
      </Box>
    </Box>
  </Dialog>
);

/** A titled block in the style of the candidate's Capabilities and Transcript tabs. */
const Section: React.FC<{ title: string; counter: string; aside?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  counter,
  aside,
  children
}) => (
  <Box
    sx={{
      p: '10px 16px',
      borderRadius: '4px',
      bgcolor: WELL,
      border: `1px solid ${LINE}`,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      '& > * + * + *': { borderTop: GRID_LINE, pt: '8px' }
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        pb: '8px',
        borderBottom: '1px solid rgba(247, 148, 32, 0.45)'
      }}
    >
      <Typography sx={{ ...BODY, fontSize: 12, color: TEXT_3, fontVariantNumeric: 'tabular-nums' }}>
        {counter}
      </Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: BRANDY, flex: 1 }}>{title}</Typography>
      {/* Pulled in so a button here doesn't make this row taller than the others. */}
      {aside && <Box sx={{ my: '-4px', display: 'flex' }}>{aside}</Box>}
    </Box>
    {children}
  </Box>
);

const RoundSetup: React.FC<{
  round: HiringRoundView;
  role: HiringRoleView | undefined;
  /** Where its Export goes: the round's header. */
  exportSlot: HTMLElement | null;
  /** Where a draft's Open bar goes: under the panel, so it never scrolls away. */
  footerSlot: HTMLElement | null;
}> = ({ round, role, exportSlot, footerSlot }) => {
  const org = useHiringOrg();
  const refresh = useRefreshHiring();
  const locked = round.status !== 'draft';
  const [picked, setPicked] = useState<string[]>([]);
  const [welcome, setWelcome] = useState(round.welcomeMessage);
  // DA-5090: what Elliott drafts the Roleplays from. The round's copy, which the role's starts it from.
  const [jobDescription, setJobDescription] = useState(round.jobDescription || '');
  const [jdToRole, setJdToRole] = useState(false);
  const [inputMode, setInputMode] = useState<'text' | 'voice'>(round.defaultInputMode);
  const [fields, setFields] = useState(round.fields);
  const [items, setItems] = useState(round.items);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState<string[]>([]);
  const [confirmDraft, setConfirmDraft] = useState(false);
  // Deselecting while Elliott drafts drops the answer when it arrives.
  const wanted = useRef(new Set<string>());

  // The server is the source of truth, so a save, a draft or an open replaces the
  // local copy. Each part follows only its own data: saving the message must not
  // throw away unsaved edits to a roleplay, or the other way round.
  useEffect(() => setWelcome(round.welcomeMessage), [round.welcomeMessage]);
  useEffect(() => setInputMode(round.defaultInputMode), [round.defaultInputMode]);
  useEffect(() => setFields(round.fields), [round.fields]);
  useEffect(() => {
    setItems(round.items);
    setPicked(
      round.items.length ? round.items.map((i) => i.capabilityId) : (role?.capabilities || []).map((c) => c.id)
    );
  }, [round.items, role]);

  const run = async (label: string, work: () => Promise<unknown>) => {
    setBusy(label);
    setError(null);
    try {
      await work();
      refresh({ roundId: round.id });
    } catch (err) {
      setError(hiringMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const build = () => run('build', () => hiringApi.buildAssessment(round.id, picked, org));
  const saveRequirements = () =>
    run('requirements', async () => {
      const saved = await hiringApi.updateRound(
        round.id,
        { defaultInputMode: inputMode, ...(locked ? {} : { fields }) },
        org
      );
      // Taken from the reply: if the server only tidied spaces, nothing refreshes and this would stay unsaved.
      setInputMode(saved.defaultInputMode);
      setFields(saved.fields);
    });
  const saveJobDescription = () =>
    run('jd', () =>
      hiringApi.updateRound(round.id, { jobDescription: jobDescription.trim(), saveJobDescriptionToRole: jdToRole }, org)
    );
  const saveWelcome = () =>
    run('welcome', async () => {
      const saved = await hiringApi.updateRound(round.id, { welcomeMessage: welcome }, org);
      setWelcome(saved.welcomeMessage);
    });
  const saveItems = () =>
    run('items', async () => {
      const saved = await hiringApi.updateRound(
        round.id,
        {
          items: items.map((i) => ({ capabilityId: i.capabilityId, roleplay: i.roleplay, quiz: i.quiz }))
        },
        org
      );
      setItems(saved.items);
    });
  // Each section has its own Save, woken only by its own changes.
  const requirementsChanged =
    inputMode !== round.defaultInputMode || JSON.stringify(fields) !== JSON.stringify(round.fields);
  const welcomeChanged = welcome !== round.welcomeMessage;
  const jdChanged = jobDescription.trim() !== (round.jobDescription || '').trim();
  const itemsChanged = JSON.stringify(items) !== JSON.stringify(round.items);
  const isFlagged = (item: HiringRoundView['items'][number]) =>
    item.roleplay !== item.draftRoleplay &&
    item.roleplayCheck?.status === 'flagged' &&
    item.roleplayCheck.text === item.roleplay;
  const flaggedCount = items.filter(isFlagged).length;
  // Elliott could not be reached when these were saved. Opening checks them again, and the
  // server refuses to open if one comes back flagged, so this is a notice rather than a block.
  const uncheckedCount = items.filter(
    (item) => item.roleplay !== item.draftRoleplay && item.roleplayCheck?.status === 'unchecked'
  ).length;
  // Opening locks all of this, so it waits until everything on screen has been saved.
  const drafting = generating.length > 0;
  const unsaved = requirementsChanged || welcomeChanged || itemsChanged || drafting;
  const open = () => run('open', () => hiringApi.openRound(round.id, org));

  // Once drafted, the last capability stays: an assessment can't be saved with none.
  const onlyItem = items.length === 1 ? items[0].capabilityId : null;
  const addItem = (item: HiringRoundView['items'][number]) => {
    const order = (role?.capabilities || []).map((c) => c.id);
    setItems((current) =>
      current.some((i) => i.capabilityId === item.capabilityId)
        ? current
        : [...current, item].sort((a, b) => order.indexOf(a.capabilityId) - order.indexOf(b.capabilityId))
    );
  };
  const draftOne = async (capabilityId: string) => {
    wanted.current.add(capabilityId);
    setGenerating((current) => [...current, capabilityId]);
    setError(null);
    try {
      const item = await hiringApi.draftItem(round.id, capabilityId, org);
      if (wanted.current.has(capabilityId)) addItem(item);
    } catch (err) {
      if (wanted.current.has(capabilityId)) {
        setPicked((current) => current.filter((id) => id !== capabilityId));
        setError(hiringMessage(err));
      }
    } finally {
      wanted.current.delete(capabilityId);
      setGenerating((current) => current.filter((id) => id !== capabilityId));
    }
  };
  // Before the first draft this is only a choice. After it, the cards follow the
  // capabilities straight away, and Save is what makes the change stick.
  const toggleCapability = (capabilityId: string) => {
    const selected = picked.includes(capabilityId);
    setPicked((current) => (selected ? current.filter((id) => id !== capabilityId) : [...current, capabilityId]));
    if (items.length === 0) return;
    if (selected) {
      wanted.current.delete(capabilityId);
      setGenerating((current) => current.filter((id) => id !== capabilityId));
      setItems((current) => current.filter((i) => i.capabilityId !== capabilityId));
      return;
    }
    const saved = round.items.find((i) => i.capabilityId === capabilityId);
    if (saved) addItem(saved);
    else draftOne(capabilityId);
  };
  const restore = (capabilityId: string) => {
    const restored = items.map((i) => (i.capabilityId === capabilityId ? { ...i, roleplay: i.draftRoleplay } : i));
    setItems(restored);
    run('items', () =>
      hiringApi.updateRound(
        round.id,
        { items: restored.map((i) => ({ capabilityId: i.capabilityId, roleplay: i.roleplay, quiz: i.quiz })) },
        org
      )
    );
  };

  const setItem = (capabilityId: string, patch: Partial<HiringRoundView['items'][number]>) =>
    setItems((current) => current.map((i) => (i.capabilityId === capabilityId ? { ...i, ...patch } : i)));

  const toggleField = (key: string) =>
    setFields((current) =>
      current.map((f) =>
        f.key === key
          ? // Selected means candidates must give it, and unselected means they aren't
            // asked. With no CV there is no CV Analysis.
            { ...f, required: !f.required }
          : f
      )
    );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {exportSlot &&
        createPortal(
          <ActionButton
            disabled={items.length === 0}
            reason={items.length === 0 ? 'Draft the assessment before exporting it' : undefined}
            onClick={() => exportAssessmentCsv(round, items)}
          >
            Export
          </ActionButton>,
          exportSlot
        )}
      {error && (
        <Typography sx={{ ...BODY, color: DANGER }} role="alert">
          {error}
        </Typography>
      )}

      {round.retentionNeedsLegalReview && (
        <Note>
          This round&apos;s hiring location and a candidate&apos;s country of residence set retention rules that
          conflict. We keep the data for the longest legal minimum ({round.retentionMonths} months) and have flagged the
          round for legal to confirm.
        </Note>
      )}

      <Section
        title="Candidate Requirements"
        counter="1/3"
        aside={
          <RowButton
            brandy={requirementsChanged}
            disabled={!requirementsChanged || busy === 'requirements'}
            onClick={saveRequirements}
          >
            {busy === 'requirements' ? 'Saving...' : 'Save'}
          </RowButton>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography sx={{ ...LABEL, color: TEXT }}>Pre Assessment</Typography>
          <Typography sx={{ ...BODY, fontSize: 12.5 }}>
            Select anything candidates must provide before they start. If CV is selected, Elliott will assess it against
            the capabilities being assessed for the role.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {fields.map((field) => (
              <Chip
                key={field.key}
                label={field.label || field.key}
                multi
                selected={field.required}
                disabled={locked}
                onClick={() => toggleField(field.key)}
              />
            ))}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography sx={{ ...LABEL, color: TEXT }}>During Assessment</Typography>
          <Typography sx={{ ...BODY, fontSize: 12.5 }}>
            The mode you would like roleplays to open in. Candidates can always switch between voice and text at any
            point. There is no time limit, and voice cannot be mandated.
          </Typography>
          {/* In a column a Segmented would stretch: this keeps it the size of its two options. */}
          <Box sx={{ alignSelf: 'flex-start' }}>
            <Segmented
              label="Which mode roleplays open in"
              options={[
                { value: 'text', label: 'Text' },
                { value: 'voice', label: 'Voice' }
              ]}
              value={inputMode}
              onChange={setInputMode}
            />
          </Box>
        </Box>
      </Section>

      <Section
        title="Welcome Message"
        counter="2/3"
        aside={
          <RowButton brandy={welcomeChanged} disabled={!welcomeChanged || busy === 'welcome'} onClick={saveWelcome}>
            {busy === 'welcome' ? 'Saving...' : 'Save'}
          </RowButton>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Typography sx={{ ...BODY, fontSize: 12.5 }}>
            What candidates read before they begin their assessment. The section beneath is included automatically. It
            covers the AI and Privacy Notices candidates must be given.
          </Typography>
          {/* One frame around what the owner writes and the notes, as candidates read them together. */}
          <Box sx={{ border: `1px solid ${LINE}`, borderRadius: '4px', bgcolor: SURFACE }}>
            <Box
              component="textarea"
              rows={2}
              aria-label="Welcome message"
              value={welcome}
              placeholder={`Thanks for applying to ${round.roleName}. This assessment takes about 30 minutes...`}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setWelcome(e.target.value)}
              sx={{
                ...TEXTAREA_SX,
                border: 0,
                bgcolor: 'transparent',
                '&:focus, &:hover': { border: 0, outline: 'none' }
              }}
            />
            <Box sx={{ mx: '12px', borderTop: `1px dashed ${LINE_2}` }} />
            <Box sx={{ p: '10px 12px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: TEXT }}>
                {round.notes.title}
              </Typography>
              <CopyLines block={round.notes} small bullets />
            </Box>
          </Box>
        </Box>
      </Section>

      <Section
        title="Capabilities to Assess"
        counter="3/3"
        aside={
          !locked && (
            <RowButton
              brandy={itemsChanged && !drafting}
              disabled={!itemsChanged || drafting || busy === 'items'}
              onClick={saveItems}
            >
              {busy === 'items' ? 'Saving...' : 'Save'}
            </RowButton>
          )
        }
      >
        {!locked && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Typography sx={{ ...BODY, fontSize: 12.5 }}>
              Taken from the role&apos;s confirmed capabilities. Deselect any that aren&apos;t relevant to hiring.
              Elliott will generate one roleplay and one quiz question per capability, which you can edit.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: TEXT }}>Job description</Typography>
              <Typography sx={{ ...BODY, fontSize: 12.5 }}>
                Elliott writes each roleplay from this, with your organisation profile and the role titles you have set up,
                so the situations describe this job. Change it before you build, then build again.
              </Typography>
              <Box
                component="textarea"
                rows={4}
                aria-label="Job description"
                value={jobDescription}
                maxLength={12000}
                placeholder="Paste the job description or describe the role: what it owns, who it works with, what the company sells and to whom."
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setJobDescription(e.target.value)}
                sx={TEXTAREA_SX}
              />
              {jdChanged && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <RowButton brandy disabled={busy === 'jd'} onClick={saveJobDescription}>
                    {busy === 'jd' ? 'Saving...' : 'Save job description'}
                  </RowButton>
                  <Box component="label" sx={{ ...BODY, fontSize: 12.5, display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={jdToRole} onChange={(e) => setJdToRole(e.target.checked)} />
                    Also save it to {round.roleName} for future rounds
                  </Box>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                [MOBILE_SX]: { flexWrap: 'wrap' }
              }}
            >
              {/* One line that scrolls across, so a long list never moves the button. */}
              <Box
                sx={{
                  display: 'flex',
                  gap: '8px',
                  flex: 1,
                  minWidth: 0,
                  overflowX: 'auto',
                  '& > *': { flexShrink: 0, whiteSpace: 'nowrap' },
                  [MOBILE_SX]: { flexBasis: '100%' }
                }}
              >
                {(role?.capabilities || []).map((capability) => (
                  <Chip
                    key={capability.id}
                    label={capability.name}
                    multi
                    selected={picked.includes(capability.id)}
                    loading={generating.includes(capability.id)}
                    disabled={busy === 'build' || (onlyItem === capability.id && !drafting)}
                    reason={onlyItem === capability.id ? 'An assessment needs at least one capability' : undefined}
                    onClick={() => toggleCapability(capability.id)}
                  />
                ))}
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ActionButton
                  primary={round.items.length === 0}
                  disabled={picked.length === 0 || busy === 'build' || drafting}
                  reason={
                    picked.length === 0
                      ? 'Choose at least one capability'
                      : drafting
                      ? 'Elliott is still drafting'
                      : undefined
                  }
                  onClick={() => (round.items.length ? setConfirmDraft(true) : build())}
                >
                  {busy === 'build'
                    ? 'Elliott is drafting...'
                    : round.items.length
                    ? 'Draft Again'
                    : 'Draft with Elliott'}
                </ActionButton>
              </Box>
            </Box>
          </Box>
        )}

        {items.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {items.map((item, itemIndex) => (
              <Box
                key={item.capabilityId}
                sx={{
                  p: '12px',
                  borderRadius: '4px',
                  bgcolor: SURFACE,
                  border: `1px solid ${LINE}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  // The title sits in the round header's brandy box; grey lines divide the parts under it.
                  '& > * + * + *': { borderTop: GRID_LINE, pt: '12px' }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    p: '8px 10px',
                    bgcolor: BRANDY_FILL,
                    border: BRANDY_FRAME,
                    borderRadius: '4px'
                  }}
                >
                  <Typography sx={{ ...BODY, fontSize: 12, color: TEXT_3, fontVariantNumeric: 'tabular-nums' }}>
                    {itemIndex + 1}/{items.length}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: TEXT }}>
                    {item.capabilityName}
                  </Typography>
                </Box>
                {locked ? (
                  // Opening the round locked these, so they read as text, not as fields.
                  <>
                    <ItemPart title="Roleplay Situation">
                      <RoleplayBrief text={item.roleplay} textSx={{ ...BODY, color: TEXT_2 }} />
                    </ItemPart>
                    <ItemPart title="Quiz Question">
                      {/* As the candidate's lesson shows it, with the correct option marked. */}
                      <QuizCard
                        instant
                        compact
                        revealCorrect
                        question={item.quiz.question}
                        options={item.quiz.options.map((label, index) => ({ id: String(index), label }))}
                        selectedId={String(item.quiz.correct)}
                        result={{
                          selectedCorrect: true,
                          correctOptionId: String(item.quiz.correct),
                          explanation: ''
                        }}
                        onSelect={() => undefined}
                      />
                    </ItemPart>
                  </>
                ) : (
                  <>
                    <ItemPart title="Roleplay Situation">
                      <Box>
                        <Box
                          component="textarea"
                          aria-label="Roleplay situation"
                          rows={item.roleplay.includes('\n') ? 12 : 3}
                          value={item.roleplay}
                          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                            setItem(item.capabilityId, { roleplay: e.target.value })
                          }
                          sx={TEXTAREA_SX}
                        />
                        <RoleplayCheckLine
                          item={item}
                          disabled={busy === 'items'}
                          onRestore={() => restore(item.capabilityId)}
                        />
                      </Box>
                      <LevelsGuide levels={item.levels} />
                    </ItemPart>
                    <ItemPart title="Quiz Question">
                      <Box
                        component="input"
                        aria-label="Quiz question"
                        value={item.quiz.question}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setItem(item.capabilityId, { quiz: { ...item.quiz, question: e.target.value } })
                        }
                        sx={INPUT_SX}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {item.quiz.options.map((option, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Box
                              component="button"
                              type="button"
                              aria-label={`Mark option ${index + 1} correct`}
                              onClick={() => setItem(item.capabilityId, { quiz: { ...item.quiz, correct: index } })}
                              sx={{
                                width: 16,
                                height: 16,
                                flexShrink: 0,
                                borderRadius: '50%',
                                cursor: 'pointer',
                                border: `1.5px solid ${item.quiz.correct === index ? '#22C55E' : TEXT_3}`,
                                bgcolor: item.quiz.correct === index ? '#22C55E' : 'transparent'
                              }}
                            />
                            <Box
                              component="input"
                              value={option}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setItem(item.capabilityId, {
                                  quiz: {
                                    ...item.quiz,
                                    options: item.quiz.options.map((o, i) => (i === index ? e.target.value : o))
                                  }
                                })
                              }
                              sx={INPUT_SX}
                            />
                          </Box>
                        ))}
                      </Box>
                    </ItemPart>
                  </>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Section>

      <DraftAgainDialog
        open={confirmDraft}
        onCancel={() => setConfirmDraft(false)}
        onConfirm={() => {
          setConfirmDraft(false);
          build();
        }}
      />

      {round.status === 'draft' &&
        footerSlot &&
        createPortal(
          <BottomBar
            title={
              flaggedCount > 0
                ? 'Fix the flagged roleplays'
                : items.length === 0
                ? 'Build Assessment'
                : unsaved
                ? 'Save Changes'
                : 'Ready to Open Round'
            }
            body={
              flaggedCount > 0
                ? `${flaggedCount} edited ${
                    flaggedCount === 1 ? 'roleplay' : 'roleplays'
                  } can't be graded on all four levels. Change the wording or restore Elliott's version to open the round.`
                : items.length === 0
                ? 'Draft the assessment with Elliott, review and save, then open the round to invite candidates.'
                : unsaved
                ? 'Save any outstanding changes to open the round.'
                : uncheckedCount > 0
                ? `Elliott couldn't check ${uncheckedCount} edited ${
                    uncheckedCount === 1 ? 'roleplay' : 'roleplays'
                  } when they were saved. Opening the round checks them again and stops if one can't be graded on all four levels.`
                : 'Opening the round locks the assessment in place and allows you to invite your candidates.'
            }
            done={items.length > 0 && flaggedCount === 0 && !unsaved}
          >
            <ActionButton
              primary
              disabled={items.length === 0 || !round.contactEmail || flaggedCount > 0 || unsaved || busy === 'open'}
              reason={
                flaggedCount > 0
                  ? 'Fix or restore the flagged roleplays first'
                  : items.length === 0
                  ? 'Build the assessment first'
                  : unsaved
                  ? 'Save your changes first'
                  : !round.contactEmail
                  ? 'Add a contact for candidates first'
                  : undefined
              }
              onClick={open}
            >
              {busy === 'open' ? 'Opening...' : 'Open Round'}
            </ActionButton>
          </BottomBar>,
          footerSlot
        )}
    </Box>
  );
};

export default RoundSetup;
